import {
  BaseTable,
  Card,
  FooterMenu,
  HeaderMenu,
  Input,
  Loading,
  PaginationData,
  TableHeaderSort,
} from "components";
import { debounce } from "lodash";
import { useState } from "react";
import { PaginationState } from "types";
import { default_pagination_value, exportPdf } from "utils";
import { Create, DeleteAll, ListItem, SelectAll } from "./components";
import { useList, ValidationSchemaType } from "./utils";
import { DeleteContext } from "pages/Setup/contexts";
import { exportExcelGroupAsset } from "./utils/functions/exportExcelGroupAsset";
import { PdfGroupAsset } from "./components/PdfGroupAsset";

export default function GroupAsset(): JSX.Element {
  const [deleteList, setDeleteList] = useState<number[]>([]);
  const [isDeleteAll, setDeleteAll] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationState>(
    default_pagination_value
  );
  const { data, isLoading, refetch } = useList(pagination);
  const onDebounce = debounce((e: any) =>
    setPagination((prev) => {
      return {
        ...prev,
        keyword: e.target.value,
      };
    })
  );

  const dataList: ValidationSchemaType[] = data.items;

  const header = [
    { title: "CODE", sort: true },
    { title: "GROUP ASSET", sort: true },
    { title: "DESCRIPTION", sort: true },
  ];

  const sortMapping: Record<string, string> = {
    CODE: "code",
    "GROUP ASSET": "name",
    DESCRIPTION: "description",
  };
  const handleSort = (item: string, sortType: string) => {
    const sortParam = sortMapping[item]
      ? `${sortMapping[item]}:${sortType}`
      : "";
    setPagination({ ...pagination, sort: sortParam, page: 1 });
  };

  return (
    <section className="container pt-5  gap-5 flex flex-col">
      <HeaderMenu title="GROUP ASSET" />

      <Card>
        <Card.Body className="flex-row justify-end items-center">
          <DeleteAll
            deleteList={deleteList}
            isDeleteAll={isDeleteAll}
            onSuccess={() => {
              refetch();
              setDeleteAll(false);
              setDeleteList([]);
            }}
          />

          <Create refetch={refetch} />
        </Card.Body>

        <Card.Body>
          <div className="flex">
            <div className="mb-3">
              <Input
                label="SEARCH"
                className="md:max-w-[300px] w-full"
                placeholder="Search..."
                onChange={onDebounce}
              />
            </div>
          </div>
          <BaseTable className="table">
            <thead>
              <tr>
                <th>NO</th>
                <SelectAll
                  deleteList={deleteList}
                  isDeleteAll={isDeleteAll}
                  toggle={setDeleteList}
                  toggleDeleteAll={setDeleteAll}
                />
                <TableHeaderSort
                  title={header}
                  onSort={(item, sortType) => handleSort(item, sortType)}
                />
                <th className="w-[150px]">ACTION</th>
              </tr>
            </thead>
            <tbody>
              <DeleteContext.Provider
                value={{
                  value: deleteList,
                  toggleDeleteData(value) {
                    // Vars
                    const list = [...deleteList];
                    const findIndex = list.findIndex((item) => item === value);

                    if (findIndex !== -1) {
                      list.splice(findIndex, 1);
                    } else {
                      list.push(value);
                    }

                    return setDeleteList(list);
                  },
                }}
              >
                {isLoading || !dataList.length ? (
                  <tr>
                    <td colSpan={6}>
                      <Loading
                        loading={isLoading}
                        errorText="No data available"
                      />
                    </td>
                  </tr>
                ) : (
                  dataList.map((item, key) => (
                    <ListItem
                      dataList={item}
                      index={key}
                      pagination={{
                        page: pagination.page,
                        size: data.pagination_data.size,
                        limit: pagination.limit,
                        total: pagination.total,
                      }}
                      refetch={refetch}
                    />
                  ))
                )}
              </DeleteContext.Provider>
            </tbody>
          </BaseTable>
          <div className="mt-5">
            <PaginationData
              data={data}
              pagination={pagination}
              setPagination={setPagination}
            />
          </div>
        </Card.Body>
      </Card>
      <FooterMenu
        export={{
          actiontype: "export",
          onClick: () => exportExcelGroupAsset({ data: dataList }),
        }}
        pdf={{
          actiontype: "pdf",
          onClick: () => {
            exportPdf({
              documentComponent: <PdfGroupAsset dataList={dataList} />,
              fileName: "group_asset",
            });
          },
        }}
      />
    </section>
  );
}
